import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { motion, useDragControls } from 'framer-motion';

const Clock = () => {
  const [time, setTime] = useState(new Date());
  const [style, setStyle] = useState<'digital' | 'minimal' | 'modern' | 'ds-digital'>('digital');
  const controls = useDragControls();

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const toggleStyle = () => {
    const styles: ('digital' | 'minimal' | 'modern' | 'ds-digital')[] = ['digital', 'minimal', 'modern', 'ds-digital'];
    const nextIndex = (styles.indexOf(style) + 1) % styles.length;
    setStyle(styles[nextIndex]);
  };

  const getDayProgress = () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
      const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
      const total = end.getTime() - start.getTime();
      const current = now.getTime() - start.getTime();
      return (current / total) * 100;
  };

  return (
    <motion.div 
      className="cursor-pointer select-none relative group"
      onClick={toggleStyle}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      drag
      dragControls={controls}
      dragConstraints={{ left: -300, right: 300, top: -200, bottom: 200 }}
    >
      <style>{`
        @font-face {
          font-family: 'DS-Digital';
          src: url('/fonts/DS-DIGITAL.ttf') format('truetype');
          font-weight: normal;
          font-style: normal;
        }
      `}</style>

      {style === 'digital' && (
        <div className="text-center">
          <div className="text-6xl md:text-8xl font-bold text-transparent bg-clip-text bg-gradient-to-b from-slate-800 to-slate-500 dark:from-white dark:to-white/50 tracking-tight font-mono drop-shadow-sm dark:drop-shadow-2xl relative">
            {format(time, 'HH:mm:ss')}
            <div className="absolute -bottom-2 left-0 w-full h-1 bg-slate-200 dark:bg-white/10 rounded-full overflow-hidden">
                 <motion.div 
                    className="h-full bg-blue-500/50"
                    animate={{ width: `${getDayProgress()}%` }}
                    transition={{ ease: "linear", duration: 1 }}
                 />
            </div>
          </div>
          <div className="text-slate-500 dark:text-slate-400 text-lg mt-4 font-light tracking-widest uppercase flex items-center justify-center gap-4">
            <span>{format(time, 'yyyy.MM.dd')}</span>
            <span className="w-1 h-1 bg-slate-400 rounded-full"></span>
            <span>{format(time, 'EEEE')}</span>
          </div>
        </div>
      )}

      {style === 'ds-digital' && (
        <div className="text-center">
          <div className="text-6xl md:text-8xl text-slate-800 dark:text-white tracking-widest drop-shadow-sm" style={{ fontFamily: 'DS-Digital, monospace' }}>
            {format(time, 'HH:mm:ss')}
          </div>
          <div className="text-slate-600 dark:text-slate-300 text-xl mt-2 tracking-widest uppercase flex items-center justify-center gap-4" style={{ fontFamily: 'DS-Digital, monospace' }}>
            <span>{format(time, 'yyyy-MM-dd')}</span>
            <span>{format(time, 'EEE')}</span>
          </div>
        </div>
      )}

      {style === 'minimal' && (
        <div className="flex flex-col items-center">
           <div className="text-6xl md:text-8xl font-thin text-slate-800 dark:text-white tracking-widest font-sans drop-shadow-sm dark:drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]">
            {format(time, 'HH:mm')}
          </div>
           <div className="w-full bg-slate-200 dark:bg-slate-800 h-1 mt-4 rounded-full overflow-hidden max-w-[200px]">
             <motion.div 
                className="h-full bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]"
                initial={{ width: 0 }}
                animate={{ width: `${(time.getSeconds() / 60) * 100}%` }}
                transition={{ type: "tween", ease: "linear" }}
             />
           </div>
        </div>
      )}

       {style === 'modern' && (
        <div className="flex items-center gap-4 bg-white/50 dark:bg-white/5 backdrop-blur-md px-8 py-6 rounded-3xl border border-slate-200 dark:border-white/10 shadow-lg dark:shadow-2xl">
           <div className="text-7xl font-bold text-slate-800 dark:text-white tracking-tighter">
            {format(time, 'HH')}
            <span className="text-blue-500 dark:text-blue-400 animate-pulse mx-1">:</span>
            {format(time, 'mm')}
          </div>
          <div className="flex flex-col border-l border-slate-300 dark:border-white/10 pl-6 gap-1">
             <span className="text-3xl font-light text-slate-600 dark:text-slate-300 tabular-nums">{format(time, 'ss')}</span>
             <span className="text-xs text-slate-500 uppercase tracking-wider font-medium">{format(time, 'EEE, MMM d')}</span>
          </div>
        </div>
      )}
      
      <div className="absolute -top-4 left-1/2 -translate-x-1/2 text-slate-500 dark:text-slate-600 text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
        按住拖动 • 点击切换样式
      </div>
    </motion.div>
  );
};

export default Clock;
